import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, TrophyIcon, ChartBarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import {
  TeamService,
  LeagueService,
  SeasonService,
  LeagueSeasonService,
  ContestantService
} from '../../services/firebaseService';
import { getUserClaimedTeams, getUserTeamsForLeague, hasUserClaimedTeam } from '../../utils/teamUtils';
import {
  isMultiEliminationWeek,
  isWeekOpenForPredictions,
} from '../../utils/predictionUtils';
import PredictionResults from '../../components/player/PredictionResults';
import { getContestantById, normalizeToContestantId } from '../../utils/contestantUtils';

export default function TeamDetail() {
  const { teamId: initialTeamId } = useParams();
  const { userProfile } = useAuth();
  
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId);
  const [team, setTeam] = useState(null);
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [leagueSeason, setLeagueSeason] = useState(null);
  const [rank, setRank] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]); // Teams user can switch to
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialTeamId && userProfile) {
      setSelectedTeamId(initialTeamId);
      loadTeamData(initialTeamId);
    }
  }, [initialTeamId, userProfile]);

  useEffect(() => {
    if (selectedTeamId && userProfile && selectedTeamId !== initialTeamId) {
      loadTeamData(selectedTeamId);
    }
  }, [selectedTeamId]);

  const loadTeamData = async (teamId) => {
    try {
      setLoading(true);
      setError('');

      // Find which league/season this team belongs to
      const claimedTeams = getUserClaimedTeams(userProfile.teams || {});
      const managedLeagues = userProfile.managedLeagues || [];
      
      // Check if user has access: either claimed teams OR managed leagues
      if (claimedTeams.length === 0 && managedLeagues.length === 0) {
        throw new Error('You must have at least one claimed team in a league or be a league manager to view team details');
      }

      // First, try to find if this is a claimed team (fast path)
      let claimedTeam = claimedTeams.find(ct => ct.teamId === teamId);
      let leagueId, seasonId;

      if (claimedTeam) {
        // User has claimed this team - use that league/season
        leagueId = claimedTeam.leagueId;
        seasonId = claimedTeam.seasonId;
      } else {
        // User hasn't claimed this team - search through their leagues to find it
        // Get unique league IDs from both claimed teams and managed leagues
        const claimedLeagueIds = [...new Set(claimedTeams.map(ct => ct.leagueId))];
        const userLeagueIds = [...new Set([...claimedLeagueIds, ...managedLeagues])];
        
        // Search through each league to find the team
        let found = false;
        for (const userLeagueId of userLeagueIds) {
          try {
            // Get all seasons for this league
            const leagueSeasons = await LeagueService.getLeagueSeasons(userLeagueId);
            
            for (const leagueSeason of leagueSeasons) {
              try {
                const testTeam = await TeamService.getTeam(userLeagueId, leagueSeason.id || leagueSeason.seasonId, teamId);
                if (testTeam) {
                  leagueId = userLeagueId;
                  seasonId = leagueSeason.id || leagueSeason.seasonId;
                  found = true;
                  break;
                }
              } catch (e) {
                // Team not in this season, continue
              }
            }
            
            if (found) break;
          } catch (e) {
            // Error loading league seasons, continue
          }
        }
        
        if (!found) {
          throw new Error('Team not found in any of your leagues');
        }
      }

      // Load team, league, season, league-season, and contestants data in parallel
      const [teamData, leagueData, seasonData, leagueSeasonData, contestantsData] = await Promise.all([
        TeamService.getTeam(leagueId, seasonId, teamId),
        LeagueService.getLeague(leagueId),
        SeasonService.getSeason(seasonId),
        LeagueSeasonService.getLeagueSeason(leagueId, seasonId),
        ContestantService.getContestantsBySeason(seasonId)
      ]);

      if (!teamData || !leagueData || !seasonData) {
        throw new Error('Team, league, or season not found');
      }

      setTeam(teamData);
      setLeague(leagueData);
      setSeason(seasonData);
      setLeagueSeason(leagueSeasonData);
      setContestants(contestantsData);

      // Calculate rank using utility function (handles ties)
      const allTeams = await TeamService.getTeamsForLeagueSeason(
        leagueId,
        seasonId
      );
      const { calculateRanks } = await import('../../utils/rankingUtils');
      const rankedTeams = calculateRanks(allTeams, (t) => t.currentScore || 0);
      const rankedTeam = rankedTeams.find(t => t.id === teamId);
      setRank(rankedTeam ? rankedTeam.rank : allTeams.length);

      // Find current open week
      const openWeeks = seasonData.weeks?.filter(w => isWeekOpenForPredictions(w)) || [];
      setCurrentWeek(openWeeks.length > 0 ? openWeeks[0] : null);

      // Get other teams user can switch to (in same league) - only if user has claimed teams in this league
      const userTeamsInLeague = getUserTeamsForLeague(userProfile.teams || {}, leagueId);
      const otherTeams = userTeamsInLeague
        .filter(ut => ut.teamId !== teamId && ut.seasonId === seasonId)
        .map(ut => {
          const teamInfo = allTeams.find(t => t.id === ut.teamId);
          return teamInfo ? { ...teamInfo, claimedAt: ut.claimedAt } : null;
        })
        .filter(Boolean);
      
      setAvailableTeams(otherTeams);

    } catch (err) {
      console.error('Error loading team data:', err);
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const getTeamScore = (teamData) => {
    return teamData?.currentScore || 0;
  };

  const getWeeklyScores = (teamData) => {
    return teamData?.weeklyScores || {};
  };

  /**
   * Check if a contestant has been eliminated
   * @param {string} contestantId - Contestant ID
   * @returns {boolean} True if eliminated
   */
  const isContestantEliminated = (contestantId) => {
    if (!season || !season.weeks || !contestantId || !contestants || contestants.length === 0) {
      return false;
    }
    
    // Get the contestant object to have both ID and name for comparison
    const contestant = getContestantById(contestantId, contestants);
    if (!contestant) {
      // If we can't find by ID, try to normalize the ID first
      const normalizedId = normalizeToContestantId(contestantId, contestants);
      if (!normalizedId) return false;
      const normalizedContestant = getContestantById(normalizedId, contestants);
      if (!normalizedContestant) return false;
      return isContestantEliminated(normalizedId); // Recursive call with normalized ID
    }
    
    const contestantIdToMatch = contestant.id;
    const contestantNameToMatch = contestant.name;
    
    // Check all weeks for eliminations
    for (const weekData of season.weeks) {
      if (!weekData.results || Object.keys(weekData.results).length === 0) continue;
      
      const results = weekData.results;
      
      // Helper function to check if a result value matches our contestant
      const matchesContestant = (resultValue) => {
        if (!resultValue) return false;
        // Try matching by ID first
        const resultId = normalizeToContestantId(resultValue, contestants);
        if (resultId === contestantIdToMatch) return true;
        // Try matching by name (in case resultValue is a name)
        const resultContestant = getContestantById(resultValue, contestants) || 
                                 contestants.find(c => c.name === resultValue);
        if (resultContestant) {
          return resultContestant.id === contestantIdToMatch || 
                 resultContestant.name === contestantNameToMatch;
        }
        // Direct string comparison as fallback
        return resultValue === contestantIdToMatch || resultValue === contestantNameToMatch;
      };
      
      // Check for finale eliminations
      if (weekData.isFinale) {
        if (results.votedOut && matchesContestant(results.votedOut)) return true;
        if (results.fireMakingLoser && matchesContestant(results.fireMakingLoser)) return true;
        // Winner is not eliminated
      }
      // Double / triple elimination (ordered votedOut array)
      else if (
        isMultiEliminationWeek(weekData) &&
        Array.isArray(results.votedOut)
      ) {
        for (const result of results.votedOut) {
          // Handle both old format (contestantId) and new format (contestantName)
          const resultValue = result.contestantId || result.contestantName || result;
          if (matchesContestant(resultValue)) return true;
        }
      }
      // Check for single elimination
      else if (results.votedOut) {
        if (matchesContestant(results.votedOut)) return true;
      }
    }
    
    return false;
  };

  /**
   * Get contestant name from ID or name
   * @param {string} contestantIdOrName - Contestant ID or name
   * @returns {string} Contestant name
   */
  const getContestantName = (contestantIdOrName) => {
    if (!contestantIdOrName) return 'N/A';
    const contestant = getContestantById(contestantIdOrName, contestants);
    return contestant ? contestant.name : contestantIdOrName;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <Link
            to="/"
            className="text-survivor-600 hover:text-survivor-700"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!team || !league || !season) {
    return null;
  }

  const weeklyScores = getWeeklyScores(team);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Homepage
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{team.name}</h1>
              <p className="text-gray-600 mt-1">{league.name} - {season.name || season.id}</p>
            </div>
            
            {/* Team Switcher (if multiple teams in league) */}
            {availableTeams.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Switch Team
                </label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => {
                    const newTeamId = e.target.value;
                    setSelectedTeamId(newTeamId);
                    // Update URL without navigation
                    window.history.replaceState({}, '', `/player/teams/${newTeamId}`);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-survivor-500 focus:border-survivor-500"
                >
                  <option value={team.id}>{team.name}</option>
                  {availableTeams.map(otherTeam => (
                    <option key={otherTeam.id} value={otherTeam.id}>
                      {otherTeam.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Team Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-500 mb-1">Total Score</div>
            <div className="text-2xl font-bold text-gray-900">{getTeamScore(team)}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-500 mb-1">Rank</div>
            <div className="text-2xl font-bold text-survivor-600">#{rank}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-500 mb-1">Current Week</div>
            <div className="text-lg font-semibold text-gray-900">
              {currentWeek ? `Week ${currentWeek.weekNumber}` : 'Closed'}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-500 mb-1">Team Members</div>
            <div className="text-lg font-semibold text-gray-900">
              {team.players?.length || 1}
            </div>
          </div>
        </div>

        {/* Weekly Score Breakdown */}
        {Object.keys(weeklyScores).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <ChartBarIcon className="h-6 w-6 mr-2" />
              Weekly Score Breakdown
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {season.weeks
                ?.filter(w => weeklyScores[w.weekNumber] !== undefined)
                .sort((a, b) => a.weekNumber - b.weekNumber)
                .map(week => (
                  <div key={week.weekNumber} className="text-center">
                    <div className="text-sm text-gray-500 mb-1">Week {week.weekNumber}</div>
                    <div className="text-lg font-bold text-survivor-600">
                      +{weeklyScores[week.weekNumber]}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Draft Picks */}
        {team.draftPicks && team.draftPicks.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <UserGroupIcon className="h-6 w-6 mr-2" />
              Draft Picks
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {team.draftPicks.map((pick, index) => {
                const contestant = getContestantById(pick.contestantId, contestants);
                const contestantName = contestant ? contestant.name : pick.contestantId || 'Unknown';
                const isEliminated = isContestantEliminated(pick.contestantId);
                
                return (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      isEliminated
                        ? 'bg-red-50 border-red-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{contestantName}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Draft Room {pick.draftRoomId || pick.weekPicked || 'N/A'}
                          {pick.pickNumber || pick.draftId
                            ? ` · Pick #${pick.pickNumber || pick.draftId}`
                            : ''}
                        </div>
                      </div>
                      <div className="ml-4">
                        {isEliminated ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Voted Out
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Still Competing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons - Only show if user has claimed this team */}
        {userProfile && hasUserClaimedTeam(userProfile.teams || {}, team.id, league.id, season.id) && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-wrap gap-3">
              {currentWeek && (
                <Link
                  to={`/predictions2/${league.id}/${season.id}?teamId=${team.id}&week=${currentWeek.weekNumber}`}
                  className="px-6 py-3 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 font-medium"
                >
                  Submit Prediction
                </Link>
              )}
              <Link
                to={`/player/standings/${league.id}/${season.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Standings
              </Link>
              <Link
                to={`/player/history/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Full History
              </Link>
            </div>
          </div>
        )}

        {/* Prediction History (Inline) */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <TrophyIcon className="h-6 w-6 mr-2" />
            Prediction History
          </h2>
          <PredictionResults
            leagueId={league.id}
            seasonId={season.id}
            teamId={selectedTeamId}
            showHeader={false}
            restrictToClosedWeeks={!hasUserClaimedTeam(userProfile?.teams || {}, team.id, league.id, season.id)}
          />
        </div>
      </div>
    </div>
  );
}

